<template>
  <div>
    <div v-if="isLoadingSummary" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading summary data...</p>
    </div>

    <div class="tna-summary-wrapper mb-4" v-if="!isLoadingSummary">
      <div class="tna-overview-card">
        <div class="tna-summary__header">
          <div>
            <span class="summary-eyebrow">This Year</span>
            <h5 class="mb-0">TNA Submission Summary</h5>
          </div>
          <div class="summary-rate">
            <strong>{{ currentApprovalPercentage }}%</strong>
            <span>TNA approval progress</span>
          </div>
        </div>

        <div class="progress summary-progress" style="height: 18px">
          <div
            class="progress-bar bg-primary"
            role="progressbar"
            :style="`width: ${currentApprovalPercentage}%;`"
            :aria-valuenow="currentApprovalPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {{ currentApprovalPercentage }}%
          </div>
        </div>

        <div class="summary-card-grid">
          <div class="summary-card is-plan">
            <div class="summary-card__icon"><i class="fe fe-target"></i></div>
            <span>TNA Plan</span>
            <strong>{{ currentSummary.plan }}</strong>
          </div>
          <div class="summary-card is-waiting">
            <div class="summary-card__icon"><i class="fe fe-clock"></i></div>
            <span>Waiting Approval</span>
            <strong>{{ currentDerived.waitingApproval }}</strong>
          </div>
          <div class="summary-card is-approved">
            <div class="summary-card__icon"><i class="fe fe-check-circle"></i></div>
            <span>TNA Approved</span>
            <strong>{{ currentSummary.approved }}</strong>
          </div>
          <div class="summary-card is-enrollment">
            <div class="summary-card__icon"><i class="fe fe-send"></i></div>
            <span>Enrolled</span>
            <strong>{{ currentDerived.enrollment }}</strong>
          </div>
          <div class="summary-card is-completed">
            <div class="summary-card__icon"><i class="fe fe-check"></i></div>
            <span>Completed</span>
            <strong>{{ currentSummary.completed }}</strong>
          </div>
        </div>
      </div>

      <div class="last-year-card">
        <span class="summary-eyebrow">Last Year</span>
        <h5 class="mb-3">TNA Reference</h5>
        <div class="last-year-list">
          <div>
            <span>Plan</span>
            <strong>{{ previousSummary.plan }}</strong>
          </div>
          <div>
            <span>Approved</span>
            <strong>{{ previousSummary.approved }}</strong>
          </div>
          <div>
            <span>Completed</span>
            <strong>{{ previousSummary.completed }}</strong>
          </div>
        </div>
        <div class="last-year-progress">
          <div :style="`width: ${previousApprovalPercentage}%;`"></div>
        </div>
        <small>{{ previousApprovalPercentage }}% TNA approval progress</small>
      </div>

      <div class="tna-roadmap-card">
        <div class="tna-summary__header">
          <div>
            <span class="summary-eyebrow">Roadmap</span>
            <h5 class="mb-0">Proses Pengajuan TNA</h5>
          </div>
        </div>

        <div class="tna-roadmap">
          <div
            v-for="(step, index) in roadmapSteps"
            :key="step.label"
            class="roadmap-step"
            :class="{ active: index === currentRoadmapIndex, done: index < currentRoadmapIndex }"
          >
            <div class="roadmap-marker">
              <i :class="step.icon"></i>
            </div>
            <div class="roadmap-copy">
              <span>{{ step.label }}</span>
              <small>{{ step.description }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Summary",
  props: {
    isLoadingSummary: {
      type: Boolean,
      default: false,
    },
    summaryData: {
      type: Object,
      default: () => ({
        previous: { plan: 0, approved: 0, completed: 0, percentage: 0 },
        current: { plan: 0, approved: 0, completed: 0, percentage: 0 },
      }),
    },
  },
  computed: {
    currentSummary() {
      return this.normalizeSummary(this.summaryData?.current);
    },
    previousSummary() {
      return this.normalizeSummary(this.summaryData?.previous);
    },
    currentDerived() {
      const waitingApproval = Math.max(this.currentSummary.plan - this.currentSummary.approved, 0);
      const enrollment = this.currentSummary.approved;

      return { waitingApproval, enrollment };
    },
    currentApprovalPercentage() {
      return this.calculateApprovalPercentage(this.currentSummary);
    },
    previousApprovalPercentage() {
      return this.calculateApprovalPercentage(this.previousSummary);
    },
    roadmapSteps() {
      return [
        {
          label: "TNA Plan",
          icon: "fe fe-target",
          description: "Training masuk ke rencana pengembangan tahun berjalan.",
        },
        {
          label: "Approval Manager",
          icon: "fe fe-user-check",
          description: "Atasan langsung review kebutuhan training karyawan.",
        },
        {
          label: "HSE Training / HR Operation Site",
          icon: "fe fe-clock",
          description: "KPLH lanjut ke HSE Training. Non-KPLH lanjut ke HR Operation Site.",
        },
        {
          label: "Approved",
          icon: "fe fe-check",
          description: "TNA sudah disetujui dan siap masuk proses enrollment.",
        },
      ];
    },
    currentRoadmapIndex() {
      if (this.currentSummary.approved > 0) return 3;
      if (this.currentDerived.waitingApproval > 0) return 1;
      if (this.currentSummary.plan > 0) return 0;

      return 0;
    },
  },
  methods: {
    normalizeSummary(summary = {}) {
      return {
        plan: Number(summary?.plan || 0),
        approved: Number(summary?.approved || 0),
        completed: Number(summary?.completed || 0),
        percentage: Number(summary?.percentage || 0),
      };
    },
    calculateApprovalPercentage(summary) {
      if (!summary?.plan) return 0;

      return Math.min(Math.round((summary.approved / summary.plan) * 100), 100);
    },
  },
};
</script>

<style>
.tna-summary-wrapper {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 16px;
}

.tna-overview-card,
.last-year-card,
.tna-roadmap-card {
  border: 1px solid #e6edf7;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.04);
}

.tna-roadmap-card {
  grid-column: 1 / -1;
}

.tna-summary__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.summary-rate {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: #5e6b7f;
}

.summary-rate strong {
  color: #b80000;
  font-size: 22px;
  line-height: 1;
}

.summary-rate span {
  font-size: 12px;
  font-weight: 700;
}

.summary-eyebrow {
  display: block;
  color: #90a4c4;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.summary-progress {
  background-color: #edf1f6;
  border-radius: 8px;
  margin-bottom: 14px;
  overflow: hidden;
}

.progress-bar {
  font-weight: 700;
  font-size: 12px;
}

.tna-summary-wrapper .summary-card-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(120px, 1fr));
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.tna-summary-wrapper .summary-card {
  min-width: 0;
  min-height: 104px;
  border-radius: 8px;
  padding: 14px;
  color: #fff;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  grid-template-rows: 1fr auto;
  align-items: center;
  gap: 8px 12px;
  box-shadow: 0 8px 20px rgba(31, 45, 61, 0.08);
}

.tna-summary-wrapper .summary-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  grid-row: 1 / span 2;
}

.tna-summary-wrapper .summary-card span {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}

.tna-summary-wrapper .summary-card strong {
  color: #fff;
  font-size: 32px;
  line-height: 1;
}

.tna-summary-wrapper .summary-card.is-plan {
  background: #f7c444;
}

.tna-summary-wrapper .summary-card.is-waiting {
  background: #e94f64;
}

.tna-summary-wrapper .summary-card.is-approved {
  background: #5bc0be;
}

.tna-summary-wrapper .summary-card.is-enrollment {
  background: #4f8ddf;
}

.tna-summary-wrapper .summary-card.is-completed {
  background: #08ce79;
}

.last-year-list {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.last-year-list div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #edf1f6;
  padding-bottom: 8px;
}

.last-year-list span,
.last-year-card small {
  color: #5e6b7f;
  font-size: 12px;
  font-weight: 700;
}

.last-year-list strong {
  color: #14213d;
  font-size: 22px;
  line-height: 1;
}

.last-year-progress {
  height: 8px;
  border-radius: 8px;
  background: #edf1f6;
  overflow: hidden;
  margin-bottom: 8px;
}

.last-year-progress div {
  height: 100%;
  border-radius: inherit;
  background: #b80000;
}

.tna-roadmap {
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  padding-top: 8px;
}

.tna-roadmap::before {
  content: "";
  position: absolute;
  top: 25px;
  left: 17px;
  right: 17px;
  height: 2px;
  background: #dbe4f0;
}

.roadmap-step {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  z-index: 1;
}

.roadmap-marker {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid #dbe4f0;
  background: #fff;
  color: #8ca0bb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-bottom: 10px;
}

.roadmap-step.done .roadmap-marker {
  border-color: #08ce79;
  color: #08a867;
  background: #eafff5;
}

.roadmap-step.active:not(.done) .roadmap-marker {
  border-color: #e94f64;
  color: #e94f64;
  background: #fff5f7;
}

.roadmap-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-right: 8px;
}

.roadmap-copy span {
  color: #14213d;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  margin-bottom: 4px;
}

.roadmap-copy small {
  color: #7c8da8;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.bg-teal {
  background-color: #5bc0be;
}

@media (max-width: 991.98px) {
  .tna-summary-wrapper {
    grid-template-columns: 1fr;
  }

  .tna-roadmap {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .tna-roadmap::before {
    display: none;
  }
}

@media (max-width: 575.98px) {
  .tna-summary__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .tna-roadmap {
    grid-template-columns: 1fr;
  }

  .summary-rate {
    align-items: flex-start;
  }
}
</style>
